import React, { useState, useEffect } from 'react';
import { Table, Button, Upload, Row, notification } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { showPreClearance, uploadVerifiedDocument } from '@app/api/pre-clearance.api';
import { PreClearanceVerifiedDocumentRequest } from '@app/models/pre_clearance/PreClearanceRequest';
import {
  PreClearanceFile,
  PreClearanceVerifiedDocumentFile,
  PreClearanceVerifiedDocumentResponse,
} from '@app/models/pre_clearance/PreClearanceResponse';

interface Certificate {
  id: number;
  key: string;
  type: 'LETTER' | 'CERTIFICATE' | 'EXTENSION';
  generatedFileUrl?: string;
  verifiedFileUrl?: string;
}

interface CertificateListProps {
  id: string | null;
  files: PreClearanceFile | PreClearanceFile[] | null; // accepts single or array
}

const CommonCertificateList: React.FC<CertificateListProps> = ({ id, files }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype || '';
  const [uploading, setUploading] = useState(false);
  const [certificateData, setCertificateData] = useState<Certificate[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!files || (typeof files === 'object' && !Array.isArray(files) && Object.keys(files).length === 0)) return;

    const fileArray = Array.isArray(files) ? files : [files];

    const transformed: Certificate[] = fileArray.map((file) => ({
      id: file.id,
      key: `${file.type}-${file.id}`,
      type: file.type as Certificate['type'],
      generatedFileUrl: file.generated_file_url || undefined,
      verifiedFileUrl:
        file.verified_file && file.verified_file_url // ← only use URL if verified_file exists
          ? file.verified_file_url
          : undefined,
    }));

    setCertificateData(transformed);
  }, [files]);

  // useEffect(() => {
  //   const fetchPreClearance = async () => {
  //     if (!id) return;
  //     try {
  //       const response = await showPreClearance(Number(id)); // API gives full pre_clearance
  //       const pc = response?.pre_clearance;

  //       if (!pc) return;

  //       const docs: Certificate[] = [];

  //       // from pre_clearance_files (can be multiple in future)
  //       if (pc.pre_clearance_files) {
  //         docs.push({
  //           key: `file-${pc.pre_clearance_files.type}`,
  //           type: pc.pre_clearance_files.type,
  //           generatedFileUrl: pc.pre_clearance_files.generated_file_url,
  //           verifiedFileUrl: pc.pre_clearance_files.verified_file_url,
  //         });
  //       }

  //       // also consider old fields (letter_name, certificate_name, extension_name)
  //       if (pc.letter_name && !docs.find((d) => d.type === 'LETTER')) {
  //         docs.push({
  //           key: 'LETTER',
  //           type: 'LETTER',
  //           generatedFileUrl: pc.letter_name, // backend already gives file name
  //         });
  //       }
  //       if (pc.certificate_name && !docs.find((d) => d.type === 'CERTIFICATE')) {
  //         docs.push({
  //           key: 'CERTIFICATE',
  //           type: 'CERTIFICATE',
  //           generatedFileUrl: pc.certificate_name,
  //         });
  //       }
  //       if (pc.extension_name && !docs.find((d) => d.type === 'EXTENSION')) {
  //         docs.push({
  //           key: 'EXTENSION',
  //           type: 'EXTENSION',
  //           generatedFileUrl: pc.extension_name,
  //         });
  //       }

  //       setCertificateData(docs);
  //     } catch (err) {
  //       console.error('Error fetching pre_clearance:', err);
  //       notification.error({ message: 'Error', description: 'Failed to fetch pre-clearance data' });
  //     }
  //   };

  //   fetchPreClearance();
  // }, [id]);

  const handleUpload = async (file: File, cert: Certificate) => {
    setUploading(true);
    try {
      const documentData: PreClearanceVerifiedDocumentRequest = { type: cert.type, file };
      const response: PreClearanceVerifiedDocumentResponse = await uploadVerifiedDocument(
        Number(id),
        documentData,
        cert.id,
      );

      const uploadedDoc = response?.data?.pc_documents?.find(
        (doc: PreClearanceVerifiedDocumentFile) => doc.id === cert.id,
      );

      if (uploadedDoc?.verified_file_url) {
        setCertificateData((prev) =>
          prev.map((c) => (c.key === cert.key ? { ...c, verifiedFileUrl: uploadedDoc.verified_file_url } : c)),
        );
        notification.success({
          message: 'Upload Success',
          description: response.message ?? 'Document uploaded successfully.',
        });
      } else {
        notification.error({
          message: 'Upload Error',
          description: 'Upload succeeded but no URL was returned.',
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      notification.error({ message: 'Upload Error', description: 'Failed to upload document.' });
    } finally {
      setUploading(false);
    }
  };

  const showCertificate = (pdfUrl: string) => {
    if (pdfUrl) {
      navigate(`/view-certificate`, { state: { id, pdfUrl } });
    } else {
      notification.error({ message: 'Error', description: 'Invalid PDF URL' });
    }
  };

  const columns = [
    {
      title: 'Document Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Generated Document',
      key: 'generated',
      render: (record: Certificate) =>
        record.generatedFileUrl ? (
          <Button size="small" type="primary" onClick={() => showCertificate(record.generatedFileUrl!)}>
            View
          </Button>
        ) : (
          'Not Available'
        ),
    },
    {
      title: 'Verified/Uploaded Document',
      key: 'verified',
      render: (record: Certificate) => (
        <Row align="middle" gutter={[8, 8]} justify="start">
          {record.verifiedFileUrl ? (
            <>
              <a href={record.verifiedFileUrl} target="_blank" rel="noopener noreferrer">
                View Uploaded {record.type}
              </a>
              {(userType === 'ADMIN' || userType === 'SUPERADMIN') && (
                <Upload
                  beforeUpload={(file) => {
                    handleUpload(file, record);
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="small"
                    loading={uploading}
                    style={{ marginLeft: '6px', padding: '2px 8px', fontSize: '12px' }}
                  >
                    Re-upload
                  </Button>
                </Upload>
              )}
            </>
          ) : (
            (userType === 'ADMIN' || userType === 'SUPERADMIN') && (
              <Upload
                beforeUpload={(file) => {
                  handleUpload(file, record);
                  return false;
                }}
                showUploadList={false}
              >
                <Button
                  icon={<UploadOutlined />}
                  size="small"
                  loading={uploading}
                  style={{ padding: '2px 8px', fontSize: '12px' }}
                >
                  Upload {record.type}
                </Button>
              </Upload>
            )
          )}
        </Row>
      ),
    },
  ];

  return <Table dataSource={certificateData} columns={columns} rowKey="key" />;
};

export default CommonCertificateList;
